import React, { useState, useEffect, useCallback } from "react";
import {
  addAffiliation,
  updateAffiliation,
  getAffiliationById,
} from "../../../services/affiliationsService";
import { validateAffiliationMaster } from "../../../utils/validation";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handleErrors } from "../../../utils/errorHandler";
import ImageUpload from "../../Common/ImageUpload/ImageUpload";
import { useMediaLibraryIntegration } from "../../MediaLibrary/MediaSourcePicker";
import FormSideDrawer from "../../Common/FormSideDrawer/FormSideDrawer";

const INITIAL_FORM_STATE = {
  affiliationName: "",
  affiliationWebsiteUrl: "",
  displayOrder: "",
  logoFile: null,
  logoPreview: "",
  affiliationLogoUrl: "",
};

const INITIAL_ERRORS_STATE = {
  affiliationName: "",
  affiliationLogoUrl: "",
  affiliationWebsiteUrl: "",
  displayOrder: "",
};

const buildAffiliationFormData = (formData, editMode, id) => {
  const payload = new FormData();
  if (editMode) {
    payload.append("Id", id);
  }
  payload.append("AffiliationName", formData.affiliationName.trim());
  if (formData.affiliationLogoUrl) {
    payload.append("AffiliationLogoUrl", String(formData.affiliationLogoUrl));
  }
  if (formData.affiliationWebsiteUrl?.trim()) {
    payload.append(
      "AffiliationWebsiteUrl",
      formData.affiliationWebsiteUrl.trim()
    );
  }
  payload.append("DisplayOrder", formData.displayOrder);
  return payload;
};

const mapAffiliationResponse = (data) => ({
  affiliationName: data.affiliationName || data.AffiliationName || "",
  affiliationWebsiteUrl:
    data.affiliationWebsiteUrl || data.AffiliationWebsiteUrl || "",
  displayOrder:
    data.displayOrder !== undefined && data.displayOrder !== null
      ? String(data.displayOrder)
      : data.DisplayOrder !== undefined && data.DisplayOrder !== null
        ? String(data.DisplayOrder)
        : "",
  logoPreview:
    data.affiliationLogoUrl ||
    data.AffiliationLogoUrl ||
    data.logoUrl ||
    "",
  logoFile: null,
});

export const AddAffiliation = ({
  editMode = false,
  initialData = {},
  onSuccess,
  setSelectedAffiliation,
  setEditMode,
  isOpen = false,
  onClose,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS_STATE);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_STATE);
    setErrors(INITIAL_ERRORS_STATE);
    setSelectedMedia(null);
  }, []);

  const closeDrawer = useCallback(() => {
    resetForm();
    setSelectedAffiliation?.(null);
    setEditMode?.(false);
    onClose?.();
  }, [resetForm, setSelectedAffiliation, setEditMode, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchData = async () => {
      const affiliationId = initialData?.id ?? initialData?.Id;
      if (editMode && affiliationId) {
        try {
          const data = await getAffiliationById(affiliationId);
          setFormData(mapAffiliationResponse(data));
          setSelectedMedia(null);
        } catch (error) {
          handleErrors(error);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
        setSelectedMedia(null);
      }
      setErrors(INITIAL_ERRORS_STATE);
    };
    fetchData();
  }, [editMode, initialData, isOpen]);

  const handleInputChange = ({ target: { name, value } }) => {
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleDisplayOrderChange = (e) => {
    const { value } = e.target;
    if (value === "" || /^[1-9]\d*$/.test(value)) {
      setFormData((prevData) => ({ ...prevData, displayOrder: value }));
      setErrors((prevErrors) => ({ ...prevErrors, displayOrder: "" }));
    }
  };

  const handleDisplayOrderKeyDown = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleMediaSelect = (media) => {
    const url = media?.mediaUrl || media?.thumbnailUrl || "";
    if (!url) return;
    setFormData((prevData) => ({
      ...prevData,
      logoFile: null,
      logoPreview: url,
      affiliationLogoUrl: url,
    }));
    setSelectedMedia(media);
    setErrors((prevErrors) => ({ ...prevErrors, affiliationLogoUrl: "" }));
  };

  const handleMediaRemove = () => {
    setFormData((prevData) => ({
      ...prevData,
      logoFile: null,
      logoPreview: "",
      affiliationLogoUrl: "",
    }));
    setSelectedMedia(null);
    setErrors((prevErrors) => ({ ...prevErrors, affiliationLogoUrl: "" }));
  };

  const logoLibrary = useMediaLibraryIntegration({
    media: selectedMedia,
    imageUrl: formData.affiliationLogoUrl,
    defaultKeyword: formData.affiliationName,
    onSelect: handleMediaSelect,
    onRemove: handleMediaRemove,
  });

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { valid, errors: validationErrors } = validateAffiliationMaster(
        formData,
        editMode
      );
      setErrors(validationErrors);

      if (!valid) return;

      try {
        setIsButtonDisabled(true);
        const affiliationId = editMode
          ? (initialData?.id ?? initialData?.Id)
          : undefined;
        const payload = buildAffiliationFormData(
          formData,
          editMode,
          affiliationId
        );
        const action = editMode ? updateAffiliation : addAffiliation;
        const response = await action(payload);

        if (response?.data?.isSuccess === false) {
          toast.error(
            response.data.message || "Request failed. Please try again."
          );
          return;
        }

        toast.success(
          `Affiliation ${editMode ? "updated" : "added"} successfully!`
        );
        resetForm();
        onSuccess?.();
        setEditMode?.(false);
        setSelectedAffiliation?.(null);
        onClose?.();
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [
      formData,
      editMode,
      initialData,
      onSuccess,
      setEditMode,
      setSelectedAffiliation,
      onClose,
      resetForm,
    ]
  );

  return (
    <FormSideDrawer
      isOpen={isOpen}
      onClose={closeDrawer}
      title={editMode ? "Update Affiliation" : "Add Affiliation"}
      subtitle={
        editMode
          ? "Edit affiliation details and save changes"
          : "Fill in the details to create a new affiliation"
      }
      icon={editMode ? "ri-pencil-line" : "ri-building-line"}
      width="md"
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={closeDrawer}
            disabled={isButtonDisabled}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="affiliation-side-form"
            className="btn btn-secondary"
            disabled={isButtonDisabled}
          >
            {isButtonDisabled
              ? editMode
                ? "Updating..."
                : "Saving..."
              : editMode
                ? "Update"
                : "Save"}
          </button>
        </>
      }
    >
      <form id="affiliation-side-form" onSubmit={handleSubmit} method="POST">
        <div className="row g-3">
          <div className="col-12">
            <label htmlFor="affiliationName" className="form-label">
              Affiliation Name <span className="required-field">*</span>
            </label>
            <input
              type="text"
              name="affiliationName"
              id="affiliationName"
              placeholder="Enter affiliation name"
              value={formData.affiliationName}
              onChange={handleInputChange}
              className={`form-control ${
                errors.affiliationName ? "is-invalid" : ""
              }`}
            />
            {errors.affiliationName && (
              <div className="invalid-feedback">{errors.affiliationName}</div>
            )}
          </div>

          <div className="col-12">
            <label htmlFor="displayOrder" className="form-label">
              Display Order <span className="required-field">*</span>
            </label>
            <input
              type="number"
              name="displayOrder"
              id="displayOrder"
              min="1"
              step="1"
              placeholder="e.g. 1"
              value={formData.displayOrder}
              onChange={handleDisplayOrderChange}
              onKeyDown={handleDisplayOrderKeyDown}
              className={`form-control ${
                errors.displayOrder ? "is-invalid" : ""
              }`}
            />
            {errors.displayOrder && (
              <div className="invalid-feedback">{errors.displayOrder}</div>
            )}
          </div>

          <div className="col-12">
            <label htmlFor="affiliationWebsiteUrl" className="form-label">
              Website URL
            </label>
            <input
              type="url"
              name="affiliationWebsiteUrl"
              id="affiliationWebsiteUrl"
              placeholder="https://example.com"
              value={formData.affiliationWebsiteUrl}
              onChange={handleInputChange}
              className={`form-control ${
                errors.affiliationWebsiteUrl ? "is-invalid" : ""
              }`}
            />
            {errors.affiliationWebsiteUrl && (
              <div className="invalid-feedback">
                {errors.affiliationWebsiteUrl}
              </div>
            )}
          </div>

          <div className="col-12">
            <ImageUpload
              label="Affiliation Logo"
              required={!editMode}
              previewUrl={formData.logoPreview}
              error={errors.affiliationLogoUrl}
              {...logoLibrary.uploadEnhancements}
            />
            {logoLibrary.modalSlot}
            {editMode ? (
              <p className="text-muted small mb-0 mt-1">
                Leave unchanged to keep the current logo.
              </p>
            ) : null}
          </div>
        </div>
      </form>
    </FormSideDrawer>
  );
};
